import { z } from "zod";
import { BASE, CUSTOM_VALUES, HEALTH, HUE, ISO, MONEY, TAG, serverOwned } from "./common.ts";
import { isIsoCurrency } from "./money.ts";

export const LIFECYCLE = z.enum(["lead", "prospect", "onboarding", "active", "paused", "offboarding", "archived"]);
export type Lifecycle = z.infer<typeof LIFECYCLE>;

/** A postal address entered as fields (no address lookup key is needed). */
export const ADDRESS = z.object({
  line1: z.string().default(""),
  line2: z.string().default(""),
  city: z.string().default(""),
  region: z.string().default(""),
  postcode: z.string().default(""),
  country: z.string().default(""),
});
export type Address = z.infer<typeof ADDRESS>;

/** Where a client first came from (X10-24a): the UTM tags a visitor arrived with, kept on the client once (first touch). */
export const ACQUISITION = z.object({
  source: z.string().trim().toLowerCase().max(120),     // utm_source, lower case: "google", "linkedin", "referral"
  medium: z.string().trim().toLowerCase().max(120).optional(),
  campaign: z.string().trim().max(120).optional(),
  term: z.string().trim().max(120).optional(),
  content: z.string().trim().max(120).optional(),
  referrer: z.string().max(500).optional(),
  landing: z.string().max(500).optional(),
  at: ISO,
});
export type Acquisition = z.infer<typeof ACQUISITION>;

export const CLIENT = serverOwned(BASE.extend({
  name: z.string(),
  slug: z.string(),
  /** Short unique code used in project names, invoice numbers and folders (FEATURES, Client codes). */
  code: z.string(),
  hue: HUE,
  type: z.enum(["contact", "lead", "client", "team"]),
  lifecycle: LIFECYCLE,
  /** The agency's own status (Settings → Client statuses); its kind is kept in `lifecycle` by the server. */
  statusId: z.string().optional(),
  custom: CUSTOM_VALUES.optional(),
  ownerId: z.string(),
  /** The parent client this one is a sub-account of (X11-809a). One level only: a parent has no parent of its own. */
  parentId: z.string().min(1).max(64).optional(),
  health: HEALTH,
  mrr: MONEY.optional(),
  outstanding: MONEY,
  country: z.string(),
  /** The client's own invoicing currency (X18-I05a): a new invoice takes it first. Null or absent: the workspace currency rules apply. */
  currency: z.string().trim().toUpperCase().length(3).refine(isIsoCurrency, "Use a currency code such as USD, EUR or AUD.").nullable().optional(),
  /** The client's own payment reminders (X18-I02). Null or absent keeps the workspace schedule (Settings, Money). When set, it
   *  replaces the workspace schedule for this client: `enabled` false sends no reminder; `steps` are the days relative to the due
   *  date, as in the workspace setting (its wording still comes from the workspace setting, by step). */
  dunning: z.object({
    enabled: z.boolean(),
    steps: z.array(z.number().int().min(-30).max(120)).max(8).refine((a) => a.every((d, i) => i === 0 || d > a[i - 1]!), "Steps must go up, one per day at most once."),
  }).nullable().optional(),
  tags: z.array(TAG),
  lastActivity: ISO,
  primaryContactId: z.string().optional(),
  onboardingPct: z.number().optional(),
  website: z.string().optional(),
  phone: z.string().optional(),
  /** Client-portal invite state: none sent, invite pending, or the portal account is active. */
  portalInvite: z.enum(["none", "invited", "active"]).optional(),
  /** Postal address (Overview, Contacts); the country is an ISO 3166 code such as AU. */
  address: ADDRESS.optional(),
  /** The client's Peppol participant (X13-0518a): the identifier scheme (ICD, such as 0151 or 0088) and the id. Peppol
   *  delivery needs it. */
  /** null clears it (the client form sends null when both parts are emptied); the Peppol send then treats it as missing. */
  peppol: z.object({ scheme: z.string().trim().min(1).max(10), id: z.string().trim().min(1).max(100) }).nullable().optional(),
  /** The e-invoice the client's sent invoices carry (X16-A08b): none, UBL 2.1 (Peppol), XRechnung 3.0 (both as an XML file
   *  beside the PDF), or Factur-X (the PDF with the CII XML inside, instead of the plain PDF). */
  einvoiceFormat: z.enum(["none", "ubl", "xrechnung", "facturx"]).default("none"),
  /** Sending an invoice also texts the first contact a short message with the pay link (X18-I06). Absent or false: no text. The text
   *  needs a connected Twilio number and the contact's phone. */
  invoiceTexts: z.boolean().optional(),
  /** Set when the client is moved to the trash (soft delete); purged 30 days later when nothing references it. */
  trashedAt: ISO.optional(),
  /** Embedded apps the client's portal shows as named tabs (X4-11, B210). The address must be https; the portal shows only
   *  the hosts the workspace allows (settings `portal.embedHosts`). */
  embeds: z.array(z.object({
    id: z.string().min(1).max(40),
    label: z.string().trim().min(1).max(60),
    url: z.string().max(500).refine((u) => { try { return new URL(u).protocol === "https:"; } catch { return false; } }, "Use an https address."),
  })).max(12).optional(),
  /** The SLA tier whose ticket targets this client gets (the inbox's SLA settings). Null or absent means the default targets. */
  tier: z.string().min(1).max(40).nullable().optional(),
  /** The first touch the client came in on (X10-24a): written once by a form with UTM tags, then kept. Staff may set it. */
  acquisition: ACQUISITION.optional(),
  /** Where the client came from: the tool it was imported from and its id there (imports only, X4-04). */
  externalRef: z.object({ source: z.string(), id: z.string() }).optional(),
  /** The accounts this client's external metrics are read from (X4-13): a GA4 property id, a Google Ads customer id, a Meta
   *  ad account id, a Search Console site URL. Absent or empty means the client has no external source yet. */
  sourceAccounts: z.object({
    ga4: z.string().max(40).optional(),
    gads: z.string().max(40).optional(),
    "meta-ads": z.string().max(40).optional(),
    gsc: z.string().max(500).optional(),
    calltracking: z.string().max(40).optional(),
    shopify: z.string().max(200).optional(),
    "linkedin-ads": z.string().max(40).optional(),
    "tiktok-ads": z.string().max(40).optional(),
    "rank-tracking": z.string().max(200).optional(),
    backlinks: z.string().max(200).optional(),
    gbp: z.string().max(200).optional(),
  }).optional(),
  /** The channel connection each session-readable source is read through (SC4-04a): a connection id per source key. */
  sourceVia: z.object({
    ga4: z.string().max(80).optional(),
    gads: z.string().max(80).optional(),
    "meta-ads": z.string().max(80).optional(),
    gsc: z.string().max(80).optional(),
    "linkedin-ads": z.string().max(80).optional(),
    "tiktok-ads": z.string().max(80).optional(),
  }).optional(),
  /** The last read of each source, written by the server only (SC4-04a): when it ran, whether it worked, and why not. */
  sourceSync: z.record(z.string(), z.object({
    at: z.string(), ok: z.boolean(), message: z.string().max(300), rows: z.number().int().optional(),
  })).optional(),
  /** The client report template (X13-1314a) whose sections the portal report lists: "seo" or "ppc". Null or absent lists every approved section. */
  /** Named reportTemplate, not reportTemplateId: schema-map reads an Id suffix as a foreign key into the templates table. */
  reportTemplate: z.string().min(1).max(40).nullable().optional(),
  /** The keyword list for rank tracking (X13-2304a): 0 to 50 keywords, each 1 to 120 characters. */
  rankKeywords: z.array(z.string().trim().min(1).max(120)).max(50).nullable().optional(),
  /** Practice data for the walkthroughs (T1-05a): set by the server only, never by a request body. */
  practice: z.boolean().optional(),
}), ["health", "outstanding", "lastActivity", "externalRef", "practice", "sourceSync"]);
export type Client = z.infer<typeof CLIENT>;

/** The name of a client's root folder: the client code, a space, the name. A client with no code gets its name alone. */
export function clientFolderName(c: { code?: string | null; name: string }): string {
  const code = (c.code ?? "").trim();
  return code ? `${code} ${c.name}` : c.name;
}

/** A client code made safe for a file name: runs of other characters become one dash; a blank or all-symbol code is none. */
const fileCode = (code?: string | null): string => (code ?? "").trim().replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

/** The file name of a report: the client code (when there is one), the report id and the date, then the extension. */
export function reportFileName(p: { clientCode?: string | null; reportId: string; stamp?: string; ext: "csv" | "pdf" }): string {
  const parts = [fileCode(p.clientCode), p.reportId, p.stamp].filter((s): s is string => !!s);
  return `${parts.join("-")}.${p.ext}`;
}

/** The title of a report: the client code, a space, the report name; with no client code, the name alone. */
export function reportTitle(name: string, clientCode?: string | null): string {
  const code = (clientCode ?? "").trim();
  return code ? `${code} ${name}` : name;
}

/** A channel a contact has opted into, with when and where the opt-in was captured. */
export const CONSENT = z.object({
  kind: z.enum(["email", "sms", "whatsapp"]),
  at: ISO,
  source: z.string(),
  /** An SMS double opt-in request not yet answered with a confirmation reply (X21-MKT-11). Not consent until it is confirmed. */
  pending: z.boolean().optional(),
});
export type Consent = z.infer<typeof CONSENT>;

/** The channel a contact prefers to be reached on (X21-RESB-09). Unset or null: no preference. */
export const PREFERRED_CONTACT = z.enum(["email", "phone", "sms", "whatsapp", "in-person"]);
export type PreferredContact = z.infer<typeof PREFERRED_CONTACT>;

export const CONTACT = serverOwned(BASE.extend({
  name: z.string(),
  preferredContact: PREFERRED_CONTACT.nullable().optional(),
  email: z.string(),
  phone: z.string().optional(),
  title: z.string().optional(),
  accountIds: z.array(z.string()),
  primaryFor: z.array(z.string()).default([]),
  /** The role ids this contact holds at its companies (X19-C08), each on the agency's contactRoles list. Set by POST /contacts/:id/roles. */
  roles: z.array(z.string().min(1).max(60)).default([]),
  timeZone: z.string(),
  image: z.string().optional(),
  consents: z.array(CONSENT).default([]),
  /** Local start and end of the contact's working day, as HH:MM. */
  workingHours: z.tuple([z.string(), z.string()]).optional(),
  custom: CUSTOM_VALUES.optional(),
  /** The portal language the contact reads (X4-11, B216). Unset: the workspace's first portal language. */
  locale: z.string().max(35).optional(),
  /** The channels the contact wants messages on (X4-11, B216). Unset: every channel. Push (X11-407b) is off unless the contact turns it on. */
  notifyPrefs: z.object({ email: z.boolean(), portal: z.boolean(), whatsapp: z.boolean(), push: z.boolean().optional() }).optional(),
  /** When the contact first opened the welcome wizard in the portal (X9-64). */
  welcomeSeenAt: ISO.optional(),
  /** Where the contact came from: the tool it was imported from and its id there (imports only, X4-04). */
  externalRef: z.object({ source: z.string(), id: z.string() }).optional(),
  /** The moment this contact's access to the workspace ends by itself (X7-04k). */
  accessEndsAt: ISO.optional(),
  /** The latest activity logged on this contact (not a task, not a plan in the future). The database keeps it (R-81). */
  lastActivityAt: ISO.optional(),
  /** Practice data for the walkthroughs (T1-05a): set by the server only. */
  practice: z.boolean().optional(),
  /** When the contact was archived (X21-RESA-01): set and cleared by POST /contacts/:id/archive and /restore only. */
  archivedAt: ISO.nullable().optional(),
}), ["lastActivityAt", "practice", "roles", "archivedAt"]);
export type Contact = z.infer<typeof CONTACT>;

/** The starting contact roles (X19-C08). The agency edits its own list in the contactRoles setting; a role on that list
 *  may be given to a contact, and the Billing role is the one invoice mail goes to. */
export const DEFAULT_CONTACT_ROLES = ([["decision-maker", "Decision maker", "iris"], ["billing", "Billing", "saffron"], ["champion", "Champion", "fern"], ["technical", "Technical", "cobalt"], ["influencer", "Influencer", "orchid"]] as const).map(([id, label, hue]) => ({ id, label, hue, archived: false }));

/** A possible duplicate between two client, two contact or two deal records, awaiting a merge or a dismissal. */
export const DEDUPE_PAIR = z.object({
  id: z.string(),
  aId: z.string(),
  bId: z.string(),
  /** Similarity from 0 to 1. */
  score: z.number().min(0).max(1),
  kind: z.enum(["client", "contact", "deal"]),
  status: z.enum(["open", "merged", "dismissed"]),
});
export type DedupePair = z.infer<typeof DEDUPE_PAIR>;

/** Something a client must do: sign, pay, approve, upload or answer (the "Waiting on you" lists). */
export const WAITING = z.object({
  id: z.string(),
  clientId: z.string(),
  kind: z.enum(["sign", "pay", "approve", "upload", "answer"]),
  title: z.string(),
  since: ISO,
  amount: MONEY.optional(),
  href: z.string(),
  /** How many reminders the agency has sent for this item. */
  nudgeCount: z.number().default(0),
  lastNudgedAt: ISO.optional(),
  /** The record the client owes something on (invoices, signers, proposals, onboardingRuns, proofs, changeRequests, assetRequests); the item is kept in step
   *  with it by the waiting.sync job and removed once the client has done it (PHASED-PLAN L17). */
  subjectType: z.string().optional(),
  subjectId: z.string().optional(),
  /** For an asset request: the request's own title, and the name of the client it is asked of (X11-816a). */
  requestTitle: z.string().optional(),
  clientName: z.string().optional(),
});
export type Waiting = z.infer<typeof WAITING>;
